import React, { Fragment } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Color } from '../../../config/Color';
import { ColoredBox, ColoredBoxProps } from '../../styles/ColoredBox';
import { Line } from '../../styles/Dividers';
import { BodySM, BodyXXS, Heading3 } from '../../styles/Typography';
import { MediaRenderer } from './MediaRenderer';
import { VideoEmbed } from './VideoEmbed';

// Animation keyframes for smooth content appearance
export const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Wrapper component for smooth fade-in animations
export const AnimatedWrapper = styled.div<{ shouldAnimate?: boolean }>`
  ${({ shouldAnimate }) =>
    shouldAnimate &&
    css`
      animation: ${fadeIn} 0.4s ease-out;
      will-change: transform, opacity;
    `}
`;

// Shared presentation content types
export type PresentationContent =
  | {
      type: 'heading';
      content: string;
      textAlign?: 'left' | 'center' | 'right' | 'justify';
    }
  | {
      type: 'custom';
      content: React.ReactElement | null;
    }
  | {
      type: 'text';
      content: string;
      textAlign?: 'left' | 'center' | 'right' | 'justify';
    }
  | {
      type: 'smalltext';
      content: string;
    }
  | {
      type: 'image';
      src: string;
      altText?: string;
      isCard?: boolean;
      maxWidth?: number;
      marginBottom?: boolean;
      layered?: boolean;
      loop?: boolean;
    }
  | {
      type: 'underline';
    }
  | {
      type: 'bulletItem';
      content: string;
    }
  | {
      type: 'outlinedBox';
      content: string;
    }
  | {
      type: 'video';
      src: string;
      title?: string;
      maxWidth?: number;
    }
  | ({ type: 'coloredBox' } & ColoredBoxProps);

/**
 * Parses a string for markdown-style bold segments (**text**) and returns
 * a mix of strings and <strong> elements. Use in config content strings, e.g.:
 * "We have applied a **30% discount** to your program."
 */
export function parseBoldInContent(text: string): React.ReactNode {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  if (parts.length === 1) return text;
  return parts.map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part));
}

// Props for the shared render content function
export type RenderContentProps = {
  content: PresentationContent;
  index: number;
  textAnimationEnabled?: boolean;
  animationIndex?: number;
  onAnimationComplete?: () => void;
  shouldAnimateNonText?: boolean;
};

// Shared render content function that all layouts can use
export function renderContent({
  content,
  index,
  textAnimationEnabled = false,
  animationIndex = 0,
  onAnimationComplete,
  shouldAnimateNonText = false,
}: RenderContentProps): React.ReactElement | null {
  switch (content.type) {
    case 'heading':
      return (
        <div style={{ margin: 'auto' }} key={index}>
          <Heading3
            data-test="heading"
            animationEnabled={textAnimationEnabled}
            animationIndex={animationIndex}
            onAnimationComplete={onAnimationComplete}
          >
            {content.content}
          </Heading3>
        </div>
      );

    case 'text':
      return (
        <div key={index} className="underline-block">
          <BodySM
            style={{ textAlign: content.textAlign }}
            animationEnabled={textAnimationEnabled}
            animationIndex={animationIndex}
            onAnimationComplete={onAnimationComplete}
          >
            {parseBoldInContent(content.content)}
          </BodySM>
        </div>
      );

    case 'smalltext':
      return (
        <BodyXXS
          key={content.content}
          animationEnabled={textAnimationEnabled}
          animationIndex={animationIndex}
          onAnimationComplete={onAnimationComplete}
        >
          {parseBoldInContent(content.content)}
        </BodyXXS>
      );

    case 'underline':
      return <Line key={index} />;

    case 'bulletItem':
      return (
        <ul key={index} style={{ textAlign: 'left', paddingLeft: '1.25rem', margin: 0 }}>
          <li style={{ color: Color.content.default }}>
            <BodySM
              animationEnabled={textAnimationEnabled}
              animationIndex={animationIndex}
              onAnimationComplete={onAnimationComplete}
            >
              {parseBoldInContent(content.content)}
            </BodySM>
          </li>
        </ul>
      );

    case 'outlinedBox':
      return (
        <OutlinedBox key={index}>
          <BodySM
            style={{ textAlign: 'left' }}
            animationEnabled={textAnimationEnabled}
            animationIndex={animationIndex}
            onAnimationComplete={onAnimationComplete}
          >
            {content.content}
          </BodySM>
        </OutlinedBox>
      );

    case 'image': {
      const { src, altText, maxWidth, marginBottom, isCard, layered, loop } = content;
      return (
        <AnimatedWrapper key={index} shouldAnimate={shouldAnimateNonText}>
          <MediaRenderer
            src={src}
            altText={altText ?? ''}
            maxWidth={maxWidth}
            marginBottom={marginBottom}
            isCard={isCard}
            layered={layered}
            loop={loop}
          />
        </AnimatedWrapper>
      );
    }

    case 'coloredBox':
      return (
        <AnimatedWrapper key={index} shouldAnimate={shouldAnimateNonText}>
          <ColoredBox
            heading={content.heading}
            title={content.title}
            body={content.body}
            textAlign={content.textAlign}
            backgroundColor={content.backgroundColor}
            borderRadius={content.borderRadius}
          />
        </AnimatedWrapper>
      );

    case 'custom':
      return <Fragment key={index}>{content.content}</Fragment>;

    case 'video':
      return (
        <AnimatedWrapper key={index} shouldAnimate={shouldAnimateNonText}>
          <VideoEmbed src={content.src} title={content.title} maxWidth={content.maxWidth} />
        </AnimatedWrapper>
      );

    default:
      return null;
  }
}

// Shared styled components that layouts can use
export const OutlinedBox = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1rem;
  margin: 1rem 0;
`;

export const Image = styled.img<{ maxWidth?: number; marginBottom?: boolean }>`
  max-width: ${({ maxWidth }) => (maxWidth !== undefined ? `${maxWidth}px` : '100%')};
  height: auto;
  display: block;
  margin: 0 auto;
  margin-bottom: ${({ marginBottom }) => (marginBottom ? '1rem' : '0')};
`;

export const ImageShadowRounded = styled(Image)`
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;
